/**
 * @author 冷 (https://github.com/LengYXin)
 * @email lengyingxin8966@gmail.com
 * @create date 2021-08-16 18:31:26
 * @modify date 2021-08-16 18:31:26
 * @desc 系统初始化登陆 用户信息信息编辑注册不要在这里写
 */
import { AuthEncryption } from '@mamba-le/auth';
import dayjs from 'dayjs';
import lodash from 'lodash';
import { BindAll } from 'lodash-decorators';
import { computed } from 'mobx';
import { forkJoin, lastValueFrom, Subject } from 'rxjs';
import { EnumAPI } from '../../apis/index';
import { EnumLoginType, EnumProtocol } from '../../enums';
import { AjaxBasics } from '../../libraries';
import { ProtocolController } from '../protocol/index';
import { onCreateFactoryAuth } from './auth';
import { onCreateFactoryUser } from './userModel';

@BindAll()
export class SystemController {
  /**
   * 认证模块 默认从 cookie 中获取 token 不需要手动设置 token
   * 除非是地址栏解析或者其他方式等等
   * @memberof SystemController
   */
  readonly Auth = onCreateFactoryAuth();

  /**
   * 用户信息
   * @memberof SystemController
   */
  readonly User = onCreateFactoryUser();
  cookieLoginOut = false; //cookie的token信息被清除，则会被设置为true，并清除登录状态

  /**
   * 用户异步数据加载订阅
   * @type {Promise<any>}
   * @memberof ControllerUser
   */
  readonly UserSubject = new Subject();

  /**
   * 协议
   * @memberof SystemController
   */
  Protocol = new ProtocolController();

  /**
   * token
   * @readonly
   * @memberof SystemController
   */
  @computed
  get AccessToken() {
    return this.Auth.AccessToken;
  }
  @computed
  get AutoLogin() {
    return lodash.get(this.User.value, '_AutoLogin', false);
  }
  @computed
  get TimeStamp() {
    return lodash.get(this.User.value, '_TimeStamp');
  }
  @computed
  get registerInfo() {
    return lodash.get(this.User.value, 'registerInfo');
  }
  @computed
  get lowPwdInfo() {
    return lodash.get(this.User.value, 'lowPwdInfo');
  }
  @computed
  get bindMobile() {
    return lodash.get(this.User.value, 'bindMobile');
  }

  @computed
  get ignoreBind() {
    return lodash.get(this.User.value, 'ignoreBind');
  }
  /**
   * 登陆状态
   * @readonly
   * @memberof SystemController
   */
  @computed
  get LoggedIn() {
    //  if (this.User.loading) {
    //    return false
    //  }
    return !lodash.isEmpty(this.AccessToken);
  }

  /**
   * 登陆
   * @param {*} formData
   * @return {*}
   * @memberof SystemController
   */
  async onLogin(body: {
    loginType?;
    username?;
    password?;
    verificationCode?;
    imgSession?;
    mobile?;
    accountRes?;
    updatePwdByLoginCode?;
    checkCode?;
    registerLoginCode?;
    identityId?;
  }) {
    try {
      await this.Auth.HydrateAsync;
      //  this.User.toggleLoading(true)
      let res = null; //登录成功后应返回 idToken + sourceAppId
      const loginType = lodash.get(body, 'loginType');

      // 手机号登录
      if (lodash.eq(loginType, EnumLoginType.mobile)) {
        res = await AjaxBasics.request(
          lodash.merge({}, EnumAPI.accountsvr_sms_login, { body }),
        );
      }

      // 注册登录
      if (
        lodash.eq(loginType, EnumLoginType.registered) ||
        lodash.eq(loginType, EnumLoginType.relRegister) ||
        lodash.eq(loginType, EnumLoginType.smbRegister)
      ) {
        res = await AjaxBasics.request({
          ...EnumAPI.accountsvr_user_add_login,
          body: { ...this.registerInfo },
        });
        // res = this.registerInfo
      }

      // 账号密码登录
      if (lodash.eq(loginType, EnumLoginType.account)) {
        if (lodash.get(body, 'accountRes')) {
          res = this.lowPwdInfo;
          this.User.merge({ lowPwdInfo: null });
        } else {
          res = await AjaxBasics.request(
            lodash.merge({}, EnumAPI.accountsvr_account_login, {
              body,
              headers: { 'img-session': lodash.get(body, 'imgSession') },
            }),
          );
          // res = lodash.assign(res, {bindMobile: true})
          // throw {status: EnumStatusCode.resetPwd}
          // 低安全密码
          if (lodash.get(res, 'updatePwdByLoginCode')) {
            res = { lowPwdInfo: res };
          }
        }
      }

      // low密码重置登录
      if (lodash.eq(loginType, EnumLoginType.pwdCode)) {
        res = await AjaxBasics.request({
          ...EnumAPI.accountsvr_account_login_update_pwd,
          body: lodash.assign(lodash.pick(body, 'password'), {
            updatePwdByLoginCode:
              lodash.get(body, 'updatePwdByLoginCode') ||
              lodash.get(this.lowPwdInfo, 'updatePwdByLoginCode'),
          }),
        });
        this.User.merge({ lowPwdInfo: null });
      }

      // 开发者平台登录
      if (lodash.eq(loginType, EnumLoginType.developer)) {
        res = await AjaxBasics.request(
          lodash.merge({}, EnumAPI.accountsvr_middle_login, {
            body,
            headers: { 'img-session': lodash.get(body, 'imgSession') },
          }),
        );
      }

      // lenovoid登录
      if (lodash.eq(loginType, EnumLoginType.lenovoid)) {
        res = await AjaxBasics.request(
          lodash.merge({}, EnumAPI.accountsvr_lenovoid_checkcode_login, {
            body,
          }),
        );
      }

      // lenovoid注册登录
      if (lodash.eq(loginType, EnumLoginType.lenovoidRegister)) {
        res = await AjaxBasics.request(
          lodash.merge({}, EnumAPI.accountsvr_lenovoid_register_login, {
            body,
          }),
        );
      }
      this.Auth.onSaveAccessToken(lodash.get(res, 'idToken'));

      this.User.merge({
        token: lodash.get(res, 'idToken'),
        _TimeStamp: Date.now(),
        ...res,
      });

      this.UserSubject.complete();

      // 保存协议记录
      try {
        if (
          lodash.includes(
            [EnumLoginType.mobile, EnumLoginType.account],
            loginType,
          )
        ) {
          await this.Protocol.onSaveProtocol({
            sceneCode: EnumProtocol.login,
            token: lodash.get(res, 'idToken'),
          });
        }
      } catch (error) {}

      return true;
    } catch (error) {
      this.UserSubject.complete();
      //  this.User.toggleLoading(false)
      console.log('LENG ~ SystemController ~ onLogin ~ this', this);
      throw error;
    }
  }

  /**
   * 校验登陆状态
   * @memberof SystemController
   */
  async onVerify() {
    //  this.User.toggleLoading(true)
    //  await this.User.HydrateAsync
    await this.Auth.HydrateAsync;
    if (this.cookieLoginOut) {
      await this.onLoginOut();
      //  this.User.toggleLoading(false)
      return;
    }
    // 如果自动登录状态为true,比较此时时间戳和记录的时间是否大于7天；如果为false,比较此时时间戳是否大于记录时间当天的0点
    if (this.AutoLogin) {
      if (dayjs().isAfter(dayjs(this.TimeStamp).add(7, 'day'))) {
        await this.onLoginOut();
        //  this.User.toggleLoading(false)
        return;
      }
    } else if (dayjs().isAfter(dayjs(this.TimeStamp).add(1, 'day'))) {
      await this.onLoginOut();
      //  this.User.toggleLoading(false)
      return;
    }

    // if (lodash.eq(lodash.get(this.User.value, 'sourceAppId'), EnumUserSourceType.bppT1)) {
    this.UserSubject.complete();
    //  this.User.toggleLoading(false)
    return;
    // }
    // this.onGetUserInfo()
  }

  /**
   * 获取用户信息，目前只查手机号
   * @returns
   */
  async onGetUserInfo() {
    if (lodash.isEmpty(this.AccessToken)) {
      this.UserSubject.complete();
      //  this.User.toggleLoading(false)
      return;
    }
    try {
      // token info
      const res = await AjaxBasics.request({
        ...EnumAPI.accountsvr_token_get_info,
        body: { queryBindMobile: true },
      });
      this.UserSubject.complete();
      this.User.merge(lodash.pick(res, ['mobile', 'userId', 'bindMobile']));
      //  this.User.toggleLoading(false)
      return res;
    } catch (error) {
      //  this.User.toggleLoading(false);
      // this.onLoginOut()
      this.UserSubject.complete();
    }
  }

  /**
   * 解析地址栏携带的登陆信息
   * @memberof SystemController
   */
  onParseQueryToken(query) {
    if (lodash.hasIn(query, 'token')) {
      const token = lodash.get(query, 'token');
      token && this.Auth.onSaveAccessToken(token);
    } else if (lodash.hasIn(query, 'share')) {
      const [token] = lodash.split(lodash.get(query, 'share'), '_');
      token && this.Auth.onSaveAccessToken(token);
    } else if (lodash.hasIn(query, 'signedUrl')) {
      // 小程序传递回来的授权 URL
      const signedUrl = lodash.get(query, 'signedUrl');
      const token = AuthEncryption.AES_Decrypt(signedUrl);
      token && this.Auth.onSaveAccessToken(token);
    }
  }

  /**
   * 退出登录
   * @return {*}
   * @memberof SystemController
   */
  async onLoginOut(noInvalid?) {
    try {
      await this.Auth.HydrateAsync;
      await lastValueFrom(
        forkJoin([
          this.loginOut(this.AccessToken, noInvalid),
          this.Auth.onClear(),
          this.User.set({}),
        ]),
      );
    } catch (error) {}
  }

  /**
   * 自动登录
   * @return {*}
   * @memberof SystemController
   */
  onAutoLogin(_AutoLogin: boolean) {
    this.User.merge({ _AutoLogin });
  }
  /**
   * 检查用户是否登陆
   * @return {*}
   * @memberof SystemController
   */
  onInspectUser() {
    if (this.LoggedIn) {
      return this.User.value;
    }
    throw '未登录';
  }
  /**
   * 退出登录接口调用
   * @return {*}
   * @memberof SystemController
   */
  async loginOut(AccessToken, noInvalid?) {
    if (AccessToken && !noInvalid) {
      await AjaxBasics.request(EnumAPI.accountsvr_loginout);
    }
  }

  /**
   * 处理保存app注入的数据
   */
  onSaveAppInfo(appInfo) {
    const { token } = appInfo;
    token && this.Auth.onSaveAccessToken(token);
  }

  /** 置换token */
  async changeToken(token) {
    if (!token) {
      return false;
    }
    await this.Auth.onSaveAccessToken(token);
  }
  
  /**
   * 获取临时token
   * @return {*}
   * @memberof SystemController
   */
  async getTemToken(body) {
    const res = await AjaxBasics.request({
      ...EnumAPI.accountsvr_tem_token,
      body,
    });
    this.Auth.onSaveAccessToken(lodash.get(res, 'idToken'));
    this.User.merge({
      token: lodash.get(res, 'idToken'),
      _TimeStamp: Date.now(),
    });
  }
}
